if (!customElements.get('quantity-input')) {
  customElements.define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', this.onButtonClick.bind(this))
        );
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      }

      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      onInputChange(event) {
        console.log('Quantity input changed:', {
          value: event.target.value,
          variantId: event.target.dataset.quantityVariantId,
          min: event.target.dataset.min,
          max: event.target.max,
          step: event.target.step
        });

        this.validateQtyRules();
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
          if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.value = parseInt(this.input.value) + parseInt(this.input.step);
          }
        } else {
          this.input.value = Math.max(0, parseInt(this.input.value) - parseInt(this.input.step));
        }

        console.log('Quantity button clicked:', {
          button: event.target.name,
          previousValue: previousValue,
          newValue: this.input.value,
          variantId: this.input.dataset.quantityVariantId
        });

        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(this.changeEvent);
        }

        // Add visual feedback for button interaction
        event.target.classList.add('quantity__button--clicked');
        setTimeout(() => {
          event.target.classList.remove('quantity__button--clicked');
        }, 150);
      }

      validateQtyRules() {
        const value = parseInt(this.input.value);
        const min = parseInt(this.input.dataset.min) || 0;
        const max = this.input.max ? parseInt(this.input.max) : null;
        const step = parseInt(this.input.step) || 1;

        console.log('Validating quantity rules:', { value, min, max, step });

        let isValid = true;
        let errorMessage = '';

        // Check minimum quantity
        if (value < min) {
          isValid = false;
          errorMessage = `Minimum quantity is ${min}`;
        }

        // Check maximum quantity
        if (max !== null && value > max) {
          isValid = false;
          errorMessage = `Maximum quantity is ${max}`;
        }

        // Check step increment
        if (value % step !== 0) {
          isValid = false;
          errorMessage = `Quantity must be in multiples of ${step}`;
        }

        // Update button states
        this.updateButtonStates(value, min, max, step);

        // Set custom validity
        this.input.setCustomValidity(isValid ? '' : errorMessage);

        if (!isValid) {
          console.warn('Quantity validation failed:', errorMessage);
        }

        return isValid;
      }

      updateButtonStates(value, min, max, step) {
        const minusButton = this.querySelector('button[name="minus"]');
        const plusButton = this.querySelector('button[name="plus"]');

        if (minusButton) {
          const wouldBeMinusValue = Math.max(0, value - step);
          minusButton.disabled = wouldBeMinusValue < min;
          minusButton.classList.toggle('quantity__button--disabled', minusButton.disabled);
        }

        if (plusButton) {
          const wouldBePlusValue = value + step;
          const maxReached = max !== null && wouldBePlusValue > max;
          plusButton.disabled = maxReached;
          plusButton.classList.toggle('quantity__button--disabled', plusButton.disabled);
        }
      }
    }
  );
}

if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mql.addEventListener('change', this.closePopover.bind(this));
        this.addEventListener('keyup', this.onKeyUp.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('focusin', this.onFocusIn.bind(this));
        this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
        this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      }

      connectedCallback() {
        setTimeout(() => {
          this.initializePopover();
        });
      }

      initializePopover() {
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.popoverToggle = this.querySelector('.quantity-popover__info-button');
        this.popoverClose = this.querySelector('.quantity-popover__info .button-close');
        this.onBodyClickEvent = this.onBodyClick.bind(this);

        if (!this.popoverToggle) return;

        this.popoverToggle.addEventListener('click', this.togglePopover.bind(this));
        this.popoverToggle.addEventListener('keydown', this.onKeyDown.bind(this));

        if (this.popoverClose) {
          this.popoverClose.addEventListener('click', this.closePopover.bind(this));
          this.popoverClose.addEventListener('keydown', this.onKeyDown.bind(this));
        }
      }

      onFocusOut() {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closePopover();
        });
      }

      onFocusIn() {
        // Keep popover open when focused
      }

      onMouseEnter() {
        const hoverContainer = this.querySelector('.quantity-popover-container--hover');
        if (hoverContainer && this.mql.matches) {
          this.openPopover();
        }
      }

      onMouseLeave() {
        const hoverContainer = this.querySelector('.quantity-popover-container--hover');
        if (hoverContainer && this.mql.matches) {
          this.closePopover();
        }
      }

      onKeyUp(event) {
        if (event.code?.toUpperCase() === 'ESCAPE') this.closePopover();
      }

      onKeyDown(event) {
        if (event.code?.toUpperCase() === 'ESCAPE') this.closePopover();
      }

      onBodyClick(event) {
        if (!this.contains(event.target)) this.closePopover();
      }

      togglePopover() {
        console.log('Toggling quantity popover');
        
        if (this.popoverInfo.hasAttribute('hidden')) {
          this.openPopover();
        } else {
          this.closePopover();
        }
      }

      openPopover() {
        console.log('Opening quantity popover');
        
        if (!this.popoverInfo || !this.popoverToggle) return;

        this.popoverInfo.removeAttribute('hidden');
        this.popoverInfo.setAttribute('aria-hidden', 'false');
        this.popoverToggle.setAttribute('aria-expanded', 'true');
        this.popoverToggle.classList.add('quantity-popover__info-button--open');
        
        // Add body click listener
        document.body.addEventListener('click', this.onBodyClickEvent);
        
        // Focus management
        const firstFocusable = this.popoverInfo.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }

      closePopover() {
        console.log('Closing quantity popover');
        
        if (!this.popoverInfo || !this.popoverToggle) return;

        this.popoverInfo.setAttribute('hidden', '');
        this.popoverInfo.setAttribute('aria-hidden', 'true');
        this.popoverToggle.setAttribute('aria-expanded', 'false');
        this.popoverToggle.classList.remove('quantity-popover__info-button--open');
        
        // Remove body click listener
        document.body.removeEventListener('click', this.onBodyClickEvent);
      }
    }
  );
}

// Enhanced quantity validation with better error handling
class QuantityValidator {
  static validate(input, showErrors = true) {
    const value = parseInt(input.value) || 0;
    const min = parseInt(input.dataset.min) || 0;
    const max = input.max ? parseInt(input.max) : null;
    const step = parseInt(input.step) || 1;
    const variantId = input.dataset.quantityVariantId;

    console.log('Validating quantity:', {
      variantId,
      value,
      min,
      max,
      step,
      showErrors
    });

    const errors = [];

    // Validate minimum
    if (value < min) {
      errors.push(`Minimum quantity is ${min}`);
    }

    // Validate maximum
    if (max !== null && value > max) {
      errors.push(`Maximum quantity is ${max}`);
    }

    // Validate step
    if (value > 0 && value % step !== 0) {
      errors.push(`Quantity must be in multiples of ${step}`);
    }

    // Validate inventory (if data available)
    const inventoryQuantity = parseInt(input.dataset.inventoryQuantity);
    if (!isNaN(inventoryQuantity) && value > inventoryQuantity) {
      errors.push(`Only ${inventoryQuantity} items available`);
    }

    const isValid = errors.length === 0;

    if (showErrors && !isValid) {
      console.warn('Quantity validation errors:', errors);
      
      // Show error in UI
      const errorContainer = input.closest('.quantity-popover')?.querySelector('.cart-item__error-text') ||
                           input.closest('cart-items')?.querySelector(`[id*="LineItemError-${input.dataset.index}"] .cart-item__error-text`);
      
      if (errorContainer) {
        errorContainer.textContent = errors[0]; // Show first error
      }

      // Set custom validity for HTML5 validation
      input.setCustomValidity(errors[0]);
    } else if (isValid) {
      input.setCustomValidity('');
      
      // Clear any existing errors
      const errorContainer = input.closest('.quantity-popover')?.querySelector('.cart-item__error-text') ||
                           input.closest('cart-items')?.querySelector(`[id*="LineItemError-${input.dataset.index}"] .cart-item__error-text`);
      
      if (errorContainer) {
        errorContainer.textContent = '';
      }
    }

    return {
      isValid,
      errors,
      value,
      min,
      max,
      step,
      variantId
    };
  }

  static getRecommendedQuantity(input) {
    const value = parseInt(input.value) || 0;
    const min = parseInt(input.dataset.min) || 0;
    const max = input.max ? parseInt(input.max) : null;
    const step = parseInt(input.step) || 1;

    if (value < min) {
      return min;
    }

    if (max !== null && value > max) {
      return max;
    }

    // Round to nearest valid step
    const remainder = value % step;
    if (remainder !== 0) {
      const lower = value - remainder;
      const upper = lower + step;
      
      // Choose closer valid value, but prefer higher if equal distance
      if (max !== null && upper > max) {
        return lower;
      }
      
      return (remainder <= step / 2) ? lower : upper;
    }

    return value;
  }
}

// Add visual feedback styles
const quantityStyles = `
  .quantity__button--clicked {
    transform: scale(0.95);
    transition: transform 0.1s ease;
  }
  
  .quantity__button--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .quantity__input--error {
    border-color: rgb(var(--color-error, 255, 0, 0));
    box-shadow: 0 0 0 1px rgb(var(--color-error, 255, 0, 0));
  }
  
  .cart-item__error-text {
    color: rgb(var(--color-error, 255, 0, 0));
    font-size: 1.2rem;
    margin-top: 0.5rem;
  }
`;

// Inject styles if not already present
if (!document.querySelector('#quantity-popover-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'quantity-popover-styles';
  styleSheet.textContent = quantityStyles;
  document.head.appendChild(styleSheet);
}

// Enhanced debounce function with better error handling
function enhancedDebounce(func, wait, options = {}) {
  let timeout;
  let lastArgs;
  let lastThis;
  const { leading = false, trailing = true, maxWait } = options;
  let maxTimeout;
  let result;
  let lastCallTime;
  let lastInvokeTime = 0;

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    
    try {
      result = func.apply(thisArg, args);
      console.log('Debounced function executed successfully');
      return result;
    } catch (error) {
      console.error('Error in debounced function:', error);
      throw error;
    }
  }

  function leadingEdge(time) {
    lastInvokeTime = time;
    timeout = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxWait !== undefined
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (lastCallTime === undefined || timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 || (maxWait !== undefined && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeout = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timeout = undefined;

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    if (maxTimeout !== undefined) {
      clearTimeout(maxTimeout);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeout = maxTimeout = undefined;
  }

  function flush() {
    return timeout === undefined ? result : trailingEdge(Date.now());
  }

  function pending() {
    return timeout !== undefined;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeout === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxWait !== undefined) {
        timeout = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timeout === undefined) {
      timeout = setTimeout(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;
  
  return debounced;
}

// Export for global use
window.QuantityValidator = QuantityValidator;
window.enhancedDebounce = enhancedDebounce;

console.log('Enhanced quantity-popover.js loaded successfully');